<template>
    <div :class="computedClasses" v-fs-block>
        <label
            v-if="hasLabel"
            @click="activate"
            class="typo__label">{{label}}</label>
        <multiselect
            ref="multiselect"
            :allow-empty="allowEmpty"
            :close-on-select="closeOnSelect"
            :custom-label="customLabel"
            :disabled="disabled"
            :hide-selected="hideSelected"
            :id="instanceId"
            :label="computedLabel"
            :loading="computedIsLoading"
            :max-height="maxHeight"
            :multiple="multiple"
            :options="computedOptions"
            :placeholder="placeholder"
            :preselect-first="preselectFirst"
            :preserve-search="preserveSearch"
            :show-labels="false"
            :taggable="taggable"
            :track-by="computedTrackBy"
            :value="computedValue"
            :internal-search="!computedIsAsync"
            :clear-on-select="clearOnSelect"
            @input="updateSelection"
            @open="open"
            @remove="onOptionRemove"
            @search-change="searchChange"
            @select="onOptionSelected"
            @tag="addTag">
            <template slot="beforeList" v-if="computedIsLoading">
                <span class="multiselect__option">Looking for matching results...</span>
            </template>
            <template v-if="requiresTagSlot" slot="tag" slot-scope="tagProps">
                <slot name="custom-tag" v-bind="tagProps"/>
            </template>
            <template v-if="computedAsyncHasPrevOptions" slot="beforeList">
                <!-- TODO fix css -->
                <li class="multiselect__option align-center multiselect__option-load multiselect__option-load-prev">
                    <v-icon-button
                        type="basic"
                        size="small"
                        @click="handleAsyncLoadPrev"
                        :disabled="computedIsLoading"
                        fluid>
                        Get prev
                        <v-icon icon="caret-up"/>
                    </v-icon-button>
                </li>
            </template>
            <template
                v-if="computedAsyncHasNextOptions"
                slot="afterList">
                <!-- TODO fix css -->
                <li class="multiselect__option align-center multiselect__option-load multiselect__option-load-next">
                    <v-icon-button
                        type="basic"
                        size="small"
                        @click="handleAsyncLoadNext"
                        :disabled="computedIsLoading"
                        fluid>
                        Load more...
                        <v-icon icon="caret-down"/>
                    </v-icon-button>
                </li>
            </template>
        </multiselect>
        <span v-if="helpText" class="field-caption" v-html="helpText"></span>
    </div>
</template>

<script>
    // vue-multiselect docs {@link https://vue-multiselect.js.org/#sub-getting-started}
    import Multiselect from 'vue-multiselect';
    import vIcon from '@/components/form/v-icon';
    import vIconButton from '@/components/form/v-icon-button';
    import AsyncInputMixin from '@/components/form/async-input-mixin';
    import shortid from 'shortid';
    import {
        findOptionByPrimitiveValue,
        getAllCachedAsyncValues,
        getOptionValue,
        getPrimitiveValueFromValue,
    } from '@/components/form/v-select/helper';

    export default {
        name: 'v-select',
        components: {vIcon, vIconButton, Multiselect},
        mixins: [new AsyncInputMixin().getMixin()],
        props: {
            /**
             * multiselect options.
             *  If `options` is array of objects, visible label/value will default to option.label/option.value
             *  unless 'optionLabel'/'optionKey' is specified
             */
            options: {
                type: Array,
                required: false,
                default: () => ([]),
            },
            /**
             * Sets property of options's label value
             */
            optionLabel: {
                type: String,
            },
            /**
             * Function to interpolate the custom label.
             * Accepts a function with the option object as the first param
             * @return {String}
             */
            customLabel: {
                type: Function,
            },
            /**
             * Sets property of options's value
             */
            optionKey: {
                type: String,
            },
            value: {
                type: [String, Array, Number, Object],
                default: null,
            },
            label: {
                type: String,
                default: null,
            },
            allowEmpty: {
                type: Boolean,
                default: false,
            },
            placeholder: {
                type: String,
            },
            validate: {
                type: Object,
                default: null,
            },
            disabled: {
                type: Boolean,
                default: false,
            },
            multiple: {
                type: Boolean,
                default: false,
            },
            preserveSearch: {
                type: Boolean,
                default: true,
            },
            hideSelected: {
                type: Boolean,
                default: false,
            },
            closeOnSelect: {
                type: Boolean,
                default: true,
            },
            preselectFirst: {
                type: Boolean,
                default: false,
            },
            helpText: {
                type: String,
            },
            autoFocus: {
                type: Boolean,
                default: false,
            },
            maxHeight: {
                type: Number,
                default: 300,
            },
            loading: Boolean,
            taggable: Boolean,
            tagValidator: Function,
            /**
             * every event is emitted with this as the second param
             */
            selectId: {
                type: String,
                required: false,
            },
        },
        computed: {
            instanceId() {
                return this.selectId || shortid.generate();
            },
            // clear input value on select value
            clearOnSelect() {
                return !this.computedIsAsync || (this.computedIsAsync && this.multiple);
            },
            // returns if options list is array with objects
            isComplexOptions() {
                if (this.computedOptions) {
                    const [firstOption] = this.computedOptions;
                    return typeof firstOption !== 'string';
                }
                return false;
            },
            primitiveValue() {
                return getPrimitiveValueFromValue({
                    value: this.computedValueProps,
                    multiple: this.multiple,
                    trackBy: this.computedTrackBy,
                });
            },
            hasLabel() {
                return (this.label || '').trim() !== '';
            },
            computedClasses() {
                return this.isInvalid ? 'is-error' : '';
            },
            isInvalid() {
                if (this.validate) {
                    return this.validate.$invalid && this.validate.$dirty;
                }
                return false;
            },
            computedOptions() {
                if (!this.computedIsAsync) {
                    return this.options;
                }
                return this.computedAsyncLastOptions || [];
            },
            computedValue() {
                const options = this.computedIsAsync
                    ? getAllCachedAsyncValues(this.async.getAllCacheItems())
                    : this.computedOptions;
                const value = this.primitiveValue;


                if (this.isComplexOptions && value !== null) {
                    if (!this.multiple) {
                        // multiple selection enabled
                        const option = findOptionByPrimitiveValue({
                            options,
                            value,
                            trackBy: this.computedTrackBy,
                        });
                        if (option) {
                            return option;
                        }
                    } else if (value) {
                        return value.map((val) => {
                            const option = options.find(opt => getOptionValue({
                                option: opt,
                                trackBy: this.computedTrackBy,
                            }) === val);
                            return option || {[this.computedTrackBy]: val, [this.computedLabel]: val};
                        });
                    }
                }
                return this.primitiveValue;
            },
            computedLabel() {
                return this.fieldOrDefaultValue('optionLabel', 'label');
            },
            computedTrackBy() {
                return this.fieldOrDefaultValue('optionKey', 'value');
            },
            requiresTagSlot() {
                return this.$slots['custom-tag'] !== undefined;
            },
        },
        methods: {
            prepareCacheValuePrefix(value) {
                if (typeof value === 'string') {
                    return value;
                }
                return String(getPrimitiveValueFromValue({
                    value,
                    multiple: this.multiple,
                    trackBy: this.computedTrackBy,
                }));
            },
            updateSelection(value) {
                let payload = value;
                if (this.isComplexOptions) {
                    if (value !== null) {
                        if (!this.multiple) {
                            payload = value[this.computedTrackBy];
                        } else {
                            payload = value.map(item => item[this.computedTrackBy]);
                        }
                    }
                    // emit original value selection
                    this.$emit('option-select', value);
                }
                this.$emit('input', payload);
            },
            addTag(newTag) {
                if (!this.taggable || (this.tagValidator && !this.tagValidator(newTag))) {
                    return;
                }
                let tag = newTag;
                if (this.isComplexOptions) {
                    tag = {
                        [this.computedLabel]: newTag,
                        [this.computedTrackBy]: newTag,
                    };
                }
                this.options.push(tag);
                this.updateSelection(this.multiple ? (this.computedValue || []).concat(tag) : tag);
            },
            fieldOrDefaultValue(prop, defaultValue) {
                if (this.isComplexOptions) {
                    return this[prop] || defaultValue;
                }
                return undefined;
            },
            activate() {
                this.$refs.multiselect.activate();
            },
            focus() {
                setTimeout(() => {
                    this.$refs.multiselect.$el.querySelector('input').focus();
                }, 200);
            },
            searchChange(event) {
                this.$emit('search-change', event);
                // handle async data load method
                if (this.computedIsAsync) {
                    this.handleAsyncFind(event);
                }
            },
            open(event) {
                this.$emit('open', event);
            },
            onOption({action, $event}) {
                this.$emit(`is-${action}`, {value: $event, instanceId: this.instanceId});
            },
            onOptionRemove($event) {
                this.onOption({action: 'removed', $event});
            },
            onOptionSelected($event) {
                this.onOption({action: 'selected', $event});
            },
        },
        mounted() {
            if (this.autoFocus) {
                this.activate();
            }
        },
    };
</script>

<!-- TODO: fix css -->
<style type="text/css">
    .multiselect__option-load {
        line-height: normal;
        padding: 0;
        min-height: auto;
        position: relative;
    }

    .multiselect__option-load-prev {
        top: -4px;
    }

    .multiselect__option-load-next {
        bottom: -4px;
    }
</style>
